import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { AiccPromptVariable } from "types/model/aiccPrompt";
import { AiccSandboxInputs } from "types/model/aiccPromptSandbox";
import { defaultSandboxInputs } from "./variableInputs";

const SANDBOX_INPUTS_VERSION = 2;

interface SandboxInputsState {
  byKey: Record<string, AiccSandboxInputs>;
  setInputs: (key: string, inputs: AiccSandboxInputs) => void;
}

export const useSandboxInputsStore = create<SandboxInputsState>()(
  persist(
    (set) => ({
      byKey: {},
      setInputs: (key, inputs) => set((state) => ({ byKey: { ...state.byKey, [key]: inputs } })),
    }),
    {
      name: "aicc-sandbox-inputs",
      storage: createJSONStorage(() => sessionStorage),
      // 🔴 Bump on a shape change: an old `{fluencyLevel, values}` record counted as answered and left every field empty.
      version: SANDBOX_INPUTS_VERSION,
      migrate: () => ({ byKey: {} } as SandboxInputsState),
    }
  )
);

export const sandboxInputsKey = (languageId: number | undefined, flowKey: string) => `${languageId}:${flowKey}`;

export function useSandboxInputs(languageId: number | undefined, flowKey: string, variables: AiccPromptVariable[]) {
  const key = sandboxInputsKey(languageId, flowKey);
  const stored = useSandboxInputsStore((state) => state.byKey[key]);
  const setInputs = useSandboxInputsStore((state) => state.setInputs);
  // 🔴 Merged per variable: all-or-nothing left a newly added variable blank forever.
  const inputs = { ...defaultSandboxInputs(variables), ...stored };

  return { inputs, setInputs: (next: AiccSandboxInputs) => setInputs(key, next) };
}
